import { NavLink, Outlet, useLocation, useNavigate, useParams } from 'react-router-dom';
import { usePainelMe } from './PainelContext';

/**
 * Moldura do painel do parceiro — fora da BottomNav do app (não é tela de
 * motorista). Rola de propósito: é tela de dados, não fluxo (exceção
 * consciente à regra "sem scroll", como Histórico e Carteira).
 *
 * Celular: cabeçalho + abas horizontais. A partir de `lg:`: menu lateral.
 */
export function PainelLayout() {
  const me = usePainelMe();
  const { orgId } = useParams();
  const { search, pathname } = useLocation();
  const navigate = useNavigate();
  // Na Rede Bow (sem org na URL) as abas da org continuam à mão: usa a última
  // org visitada (ou a primeira) pra montar a navegação.
  const lembrada = (() => {
    try {
      return sessionStorage.getItem('bow-painel-org');
    } catch {
      return null;
    }
  })();
  const org =
    me.orgs.find((o) => o.id === orgId) ??
    (orgId ? null : me.orgs.find((o) => o.id === lembrada) ?? me.orgs[0] ?? null);
  if (orgId && org) {
    try {
      sessionStorage.setItem('bow-painel-org', org.id);
    } catch {
      /* storage indisponível: só perde a lembrança */
    }
  }

  const tabs = org
    ? [
        { to: `/painel/${org.id}`, label: 'Visão geral', end: true },
        { to: `/painel/${org.id}/estacoes`, label: 'Ao vivo', end: false },
        { to: `/painel/${org.id}/sessoes`, label: 'Recargas', end: false },
        { to: `/painel/${org.id}/financeiro`, label: 'Financeiro', end: false }
      ]
    : [];
  if (me.network_view) tabs.push({ to: '/painel/rede', label: 'Rede Bow', end: false });

  // Período segue entre abas; a página das recargas não leva junto.
  const keep = new URLSearchParams(search);
  keep.delete('page');
  const qs = keep.toString() ? `?${keep.toString()}` : '';

  const tabClass = ({ isActive }: { isActive: boolean }) =>
    `shrink-0 rounded-full px-4 py-2 text-[14px] font-semibold transition lg:rounded-xl lg:px-3 lg:py-2.5 ${
      isActive ? 'bg-bow-royal text-bow-cream' : 'text-bow-royal/65 hover:bg-bow-royal/5'
    }`;

  return (
    <div className="min-h-screen bg-bow-cream text-bow-royal">
      <div
        className="mx-auto flex max-w-6xl flex-col lg:flex-row lg:gap-8 lg:px-6"
        style={{ paddingTop: 'max(1rem, env(safe-area-inset-top))' }}
      >
        <aside className="px-4 lg:sticky lg:top-6 lg:w-56 lg:shrink-0 lg:self-start lg:px-0">
          <button
            type="button"
            onClick={() => navigate('/profile')}
            className="mb-3 inline-flex items-center gap-1 text-[13px] font-semibold text-bow-royal/55"
          >
            ← Voltar ao app
          </button>
          <span className="block text-[11px] font-semibold uppercase tracking-[0.06em] text-bow-royal/50">
            Painel do parceiro
          </span>
          {me.orgs.length > 1 ? (
            <select
              aria-label="Organização"
              value={org?.id ?? ''}
              onChange={(e) => e.target.value && navigate(`/painel/${e.target.value}${qs}`)}
              className="mt-1 w-full rounded-xl border border-bow-cream-3 bg-white px-3 py-2 text-[15px] font-semibold"
            >
              {!org && <option value="">Escolha a organização</option>}
              {me.orgs.map((o) => (
                <option key={o.id} value={o.id}>
                  {o.name}
                </option>
              ))}
            </select>
          ) : (
            <h1 className="m-0 mt-1 text-[22px] font-semibold tracking-[-0.02em]">
              {org?.name ?? me.orgs[0]?.name}
            </h1>
          )}
          {org && (
            <p className="m-0 mt-1 text-[12.5px] text-bow-royal/55">
              {org.kind === 'cpo' ? 'Operador (CPO)' : 'Investidor'}
            </p>
          )}
          <nav
            aria-label="Seções do painel"
            className="-mx-4 mt-4 flex gap-1 overflow-x-auto px-4 pb-2 lg:mx-0 lg:flex-col lg:px-0"
          >
            {tabs.map((t) => (
              <NavLink key={t.to} to={`${t.to}${t.to.endsWith('/estacoes') ? '' : qs}`} end={t.end} className={tabClass}>
                {t.label}
              </NavLink>
            ))}
          </nav>
        </aside>
        <main
          key={pathname}
          className="min-w-0 flex-1 px-4 pb-16 lg:px-0"
          style={{ paddingBottom: 'max(4rem, env(safe-area-inset-bottom))' }}
        >
          <Outlet />
        </main>
      </div>
    </div>
  );
}
